import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Contexts
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider, useI18n } from "./contexts/I18nContext";
import { FlagProvider } from "./contexts/FlagContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Editor from "./pages/Editor";
import GamePlay from "./pages/GamePlay";
import Gallery from "./pages/Gallery";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Tutorial from "./pages/Tutorial";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import AdminDashboard from "./pages/AdminDashboard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Templates from "./pages/Templates";
import PaymentResult from "./pages/PaymentResult";
import Trash from "./pages/Trash";
import Assets from "./pages/Assets";
import Marketplace from "./pages/Marketplace";
import Chat from "./pages/Chat";
import Messages from "./pages/Messages";
import Premium from "./pages/Premium";
import Library from "./pages/Library";
import Generator from "./pages/Generator";

// Components
import AuthCallback from "./components/AuthCallback";
import SplashScreen from "./components/SplashScreen";
import NavDock from "./components/NavDock";
import LanguagePill from "./components/LanguagePill";
import AutoTranslate from "./components/AutoTranslate";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center animate-pulse">
          <div className="w-6 h-6 rounded-full border-2 border-[#06B6D4] border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Splash Screen Wrapper — bypass for public policy/legal + game-play routes so first-time
// visitors can read them without any gate (Play Store & GDPR requirement).
const PUBLIC_BYPASS_PREFIXES = ['/privacy', '/terms', '/faq', '/contact', '/play/', '/gallery', '/pricing', '/templates', '/tutorial', '/payment/', '/login', '/register', '/marketplace', '/premium'];
const SplashWrapper = ({ children }) => {
  const { showSplash } = useI18n();
  const location = useLocation();
  const bypass = PUBLIC_BYPASS_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p));

  if (showSplash && !bypass) {
    return <SplashScreen />;
  }
  return children;
};

// App Router with Auth Callback detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/play/:projectId" element={<GamePlay />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/templates" element={<Templates />} />
      <Route path="/tutorial" element={<Tutorial />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/help" element={<FAQ />} />
      <Route path="/payment/success" element={<PaymentResult mode="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/editor/:projectId" element={<ProtectedRoute><Editor /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/trash" element={<ProtectedRoute><Trash /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/generate" element={<ProtectedRoute><Generator /></ProtectedRoute>} />
      <Route path="/premium" element={<Premium />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <FlagProvider>
          <div className="App">
            <BrowserRouter>
              <AutoTranslate />
              <LanguagePill />
              <SplashWrapper>
                <AppRouter />
              </SplashWrapper>
              <NavDock />
            </BrowserRouter>
            <Toaster position="bottom-right" theme="dark" />
          </div>
        </FlagProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
